import { useEffect, useState } from 'react';
import { api, ErreurApi } from './api';
import type {
  CouvertureRelief, EtatEtapes, FicheScenario, LectureSimulation, ScenarioResume,
} from './api';
import { Maquette } from './maquette';

/**
 * PROSPECTIVE (§20-§25, §28-§30) — LA CHAÎNE DU §21, DE BOUT EN BOUT.
 *
 * ⚠️⚠️ L'ÉCRAN NE COMPTE PLUS SES ÉTAPES : IL LES DEMANDE. L'item D.1491 l'a
 * mesuré — cette page portait un compte d'étapes EN DUR, figé à l'étape 2,
 * pendant qu'elle appelait elle-même programme et masterplan : la prose
 * n'avait pas suivi le code. Le compte vient désormais de
 * GET /prospective/etapes, qui lit le routeur VIVANT : une route retirée fait
 * baisser le chiffre affiché ici, sans qu'on touche à cette page.
 * (On ne recopie pas la phrase morte ici non plus : la documentation d'un
 * défaut qui cite sa forme exacte nourrit les gardes qui la cherchent.)
 *
 * ⚠️ LA MESURE NE SE DONNE JAMAIS SANS SES LIMITES. Chaque moteur rend ses
 * normes non établies, ses avertissements et ses refus NOMMÉS — l'écran les
 * affiche AVANT les chiffres, parce qu'une réserve lue après coup n'a jamais
 * retenu personne.
 */

const nf = (v: number | undefined | null) => (v ?? 0).toLocaleString('fr-FR');

export function Prospective() {
  const [couverture, setCouverture] = useState<CouvertureRelief | null>(null);
  const [etapes, setEtapes] = useState<EtatEtapes | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [zone, setZone] = useState({ ouest: -13.71, sud: 9.51, est: -13.67, nord: 9.55 });
  const [simulation, setSimulation] = useState<LectureSimulation | null>(null);
  const [encours, setEncours] = useState(false);
  /** §20 — le mode est une QUESTION posée au territoire, enregistrée avec l'étude. */
  const [mode, setMode] = useState<'creer' | 'transformer' | 'simuler'>('creer');
  const [forme, setForme] = useState<Record<string, number>>({
    niveaux: 3, empriseAuSolPct: 50, pasSecondaireM: 100,
    parcelleLargeurM: 15, parcelleProfondeurM: 25, orientationDeg: 0,
  });
  // ── scénarios persistés ────────────────────────────────────────────────────
  const [titre, setTitre] = useState('');
  const [hypotheses, setHypotheses] = useState('');
  const [depotEnCours, setDepotEnCours] = useState(false);
  const [depotMessage, setDepotMessage] = useState<string | null>(null);
  const [scenarios, setScenarios] = useState<ScenarioResume[] | null>(null);
  const [compares, setCompares] = useState<FicheScenario[]>([]);

  const [scenariosTotal, setScenariosTotal] = useState(0);
  const [scenariosErreur, setScenariosErreur] = useState<string | null>(null);
  const chargerScenarios = () =>
    api.prospective.scenarios.lister()
      .then((r) => { setScenarios(r.scenarios); setScenariosTotal(r.total); setScenariosErreur(null); })
      .catch((e) => {
        // ⚠️ UN REFUS N'EST PAS UNE PANNE. Sans permission, la section se tait
        // (le dépôt, lui, parlera) ; toute AUTRE erreur se DIT — un silence
        // uniforme ferait lire une base injoignable comme un droit manquant.
        setScenarios(null);
        setScenariosErreur(e instanceof ErreurApi && e.estRefus ? null
          : 'La liste des scénarios n\'a pas pu être lue — ce n\'est pas un problème de droits.');
      });

  useEffect(() => {
    api.prospective.couverture()
      .then(setCouverture)
      .catch((e) => setErreur(e instanceof ErreurApi ? e.message : 'Couverture du relief illisible.'));
    api.prospective.etapes()
      .then(setEtapes)
      .catch(() => setEtapes(null));
    void chargerScenarios();
  }, []);

  const analyser = async () => {
    setEncours(true); setErreur(null); setSimulation(null); setDepotMessage(null);
    try {
      // ⚠️ UN SEUL APPEL : /simulation rejoue la chaîne entière du §21 et rend
      // chaque étape ENTIÈRE. Chaque étape garde pourtant sa propre route —
      // c'est la ligne de vérification de D.1491, pas une redondance.
      const s = await api.prospective.simulation(zone.ouest, zone.sud, zone.est, zone.nord, forme);
      setSimulation(s);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "L'analyse du territoire a échoué.");
    } finally { setEncours(false); }
  };

  const enregistrer = async () => {
    setDepotEnCours(true); setDepotMessage(null);
    try {
      const r = await api.prospective.scenarios.deposer({
        titre, mode, ...zone, parametres: forme, hypotheses,
      });
      setDepotMessage(`Scénario ${r.reference} enregistré — le serveur a refait la chaîne entière `
        + `et a figé son bilan (${r.viable ? 'viable' : 'NON viable'} au sens du §24).`);
      setTitre(''); setHypotheses('');
      await chargerScenarios();
    } catch (e) {
      setDepotMessage(e instanceof ErreurApi ? e.message : "L'enregistrement a échoué.");
    } finally { setDepotEnCours(false); }
  };

  const retirer = async (reference: string) => {
    setDepotMessage(null);
    try {
      await api.prospective.scenarios.retirer(reference);
      setDepotMessage(`Scénario ${reference} retiré — il reste retrouvable : on ne supprime pas.`);
      await chargerScenarios();
    } catch (e) {
      // Le serveur garde la porte (pros.scenario:retirer) : son refus se
      // montre tel quel, il nomme le droit qui manque.
      setDepotMessage(e instanceof ErreurApi ? e.message : 'Le retrait a échoué.');
    }
  };

  const comparer = async (reference: string) => {
    if (compares.some((f) => f.reference === reference)) {
      setCompares(compares.filter((f) => f.reference !== reference));
      return;
    }
    try {
      const fiche = await api.prospective.scenarios.fiche(reference);
      setCompares([...compares.slice(-2), fiche]); // trois au plus : au-delà, le tableau ne se lit plus
    } catch (e) {
      setDepotMessage(e instanceof ErreurApi ? e.message : 'Fiche illisible.');
    }
  };

  const champ = (cle: keyof typeof zone, libelle: string) => (
    <label className="champ-zone">
      <span>{libelle}</span>
      <input type="number" step="0.01" value={zone[cle]}
        onChange={(ev) => setZone({ ...zone, [cle]: Number(ev.target.value) })} />
    </label>
  );

  const s = simulation && simulation.etat === 'mesure' ? simulation : null;
  const plan = s?.etapes?.plan && s.etapes.plan.etat === 'mesure' ? s.etapes.plan : null;
  const contraintes = s?.etapes?.contraintes && s.etapes.contraintes.etat === 'mesure' ? s.etapes.contraintes : null;
  const programme = s?.etapes?.programme && s.etapes.programme.etat === 'mesure' ? s.etapes.programme : null;
  const infra = s?.etapes?.infrastructures;
  const mobilite = s?.etapes?.mobilite;
  const quartiers = s?.etapes?.quartiers;
  const batiments = s?.etapes?.batiments;
  const reseaux = s?.etapes?.reseaux;

  return (
    <section className="panneau">
      <header className="panneau-tete">
        <h2>Prospective — la chaîne du §21</h2>
        {/* ⚠️ LE COMPTE VIENT DU SERVICE, ET LES ABSENTES RESTENT EN TÊTE tant
            qu'il en reste. Si l'appel échoue, on le DIT — un en-tête muet se
            lirait comme une chaîne complète. */}
        {etapes === null ? (
          <p className="panneau-avertissement">
            L'état des étapes n'a pas pu être lu : impossible de dire ce qui est en service.
          </p>
        ) : (
          <p className="panneau-avertissement">
            <strong>{etapes.servies} étape{etapes.servies > 1 ? 's' : ''} sur {etapes.total} en service</strong>
            {' '}— comptées sur les routes réellement servies, pas déclarées ici.{' '}
            {etapes.absentes.length > 0 ? (
              <>Absentes : {etapes.absentes.join(', ')}.</>
            ) : (
              <>Chaque étape rend une ÉTUDE : elle éclaire une décision, elle ne crée ni droit,
              ni autorisation, ni servitude.</>
            )}
          </p>
        )}
      </header>

      {couverture && (
        <div className="bandeau-couverture">
          {couverture.tuiles === 0 ? (
            <span className="avert">Aucune tuile de relief au registre — le terrain n'est pas versé.</span>
          ) : !couverture.volumeMonte ? (
            <span className="avert">
              {couverture.tuiles} tuiles au registre, mais le volume n'est pas monté :
              aucune altitude ne pourra être lue.
            </span>
          ) : (
            <span>
              {couverture.tuiles} tuiles · {(couverture.octets / 1073741824).toFixed(2)} Gio ·
              altitudes {couverture.altitudeMinM?.toFixed(0)} à {couverture.altitudeMaxM?.toFixed(0)} m
              {couverture.verseLe && ` · versé le ${new Date(couverture.verseLe).toLocaleDateString('fr-FR')}`}
            </span>
          )}
        </div>
      )}

      <div className="zone-saisie">
        {champ('ouest', 'Ouest')}{champ('sud', 'Sud')}{champ('est', 'Est')}{champ('nord', 'Nord')}
        <button className="bouton-principal" onClick={analyser} disabled={encours}>
          {encours ? 'Simulation…' : 'Simuler le territoire'}
        </button>
      </div>

      {/* §20 — les trois questions. ⚠️ Les moteurs sont LES MÊMES pour les
          trois : le mode qualifie la QUESTION posée (et l'étude enregistrée),
          il ne change pas le calcul — le prétendre serait une façade. */}
      <div className="reglages-forme">
        {([
          ['creer', 'Créer une ville', 'terrain nu : que peut-il devenir ?'],
          ['transformer', 'Transformer l\'existant', 'ce qui occupe déjà le sol est mesuré au registre'],
          ['simuler', 'Tester un futur', 'une hypothèse à comparer à d\'autres'],
        ] as const).map(([cle, libelle, aide]) => (
          <label key={cle} className="reglage">
            <span>
              <input type="radio" name="mode" checked={mode === cle} onChange={() => setMode(cle)} />
              {' '}{libelle}
            </span>
            <em>{aide}</em>
          </label>
        ))}
      </div>

      <div className="reglages-forme">
        {([
          ['niveaux', 'Niveaux (R+N)', 1, 40, 1],
          ['empriseAuSolPct', 'Emprise au sol', 5, 90, 5],
          ['pasSecondaireM', 'Côté d’îlot (m)', 40, 400, 10],
          ['parcelleLargeurM', 'Largeur parcelle (m)', 5, 80, 1],
          ['parcelleProfondeurM', 'Profondeur parcelle (m)', 8, 120, 1],
          ['orientationDeg', 'Orientation de la trame', 0, 179, 5],
        ] as [string, string, number, number, number][]).map(([cle, libelle, min, max, pas]) => (
          <label key={cle} className="reglage">
            <span>{libelle}<b>{forme[cle]}</b></span>
            <input type="range" min={min} max={max} step={pas} value={forme[cle]}
              onChange={(ev) => setForme({ ...forme, [cle]: Number(ev.target.value) })} />
          </label>
        ))}
      </div>

      {erreur && <div className="carte-alerte" role="alert">{erreur}</div>}

      {simulation && simulation.etat !== 'mesure' && (
        <div className="carte-alerte" role="status">
          {simulation.etat === 'relief_absent' && "Le relief n'est pas installé sur ce service."}
          {simulation.etat === 'hors_couverture' &&
            `Zone hors couverture — tuiles manquantes : ${(simulation.tuilesManquantes ?? []).join(', ')}`}
          {simulation.etat === 'emprise_invalide' && `Emprise refusée : ${simulation.motif}`}
          {simulation.etat === 'normes_absentes' &&
            `Normes absentes du référentiel : ${(simulation.manquantes ?? []).join(', ')}. `
            + 'Rien ne se chiffre sans elles.'}
        </div>
      )}

      {s && s.viabilite && (
        <div className={s.viabilite.viable ? 'verdict verdict-ok' : 'verdict verdict-non'} role="status">
          {/* §24 — LE VERDICT PASSE AVANT LA MAQUETTE : une ville séduisante en
              3D dont le drainage ne coule nulle part doit être jugée AVANT
              d'être admirée. */}
          <strong>{s.viabilite.viable ? 'Essentiels pris en compte (§24)' : 'NON VIABLE au sens du §24'}</strong>
          <p>{s.viabilite.lecture}</p>
          <table className="table-normes">
            <tbody>
              {s.viabilite.essentiels.map((e) => (
                <tr key={e.code}>
                  <td>{e.libelle}</td>
                  <td className={e.couvert ? 'norme-etablie' : 'norme-non-etablie'}>
                    {e.couvert ? 'pris en compte' : 'NON couvert'}
                  </td>
                  <td className="ref-legale">{e.couvert ? e.mesure : `${e.mesure} — ${e.manque}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {s && !s.fondeEnDroit && (
        <div className="bandeau-non-fonde" role="note">
          <strong>Non fondé en droit.</strong>{' '}
          Des normes consommées par cette chaîne ne sont établies par aucun texte — chaque étape
          nomme les siennes ci-dessous. Ces chiffres sont un ordre de grandeur de travail :
          ils n'engagent pas l'État et ne peuvent fonder ni autorisation, ni servitude,
          ni document opposable.
        </div>
      )}

      {plan && (
        <div className="resultat-terrain">
          <h3>Maquette du plan — §22</h3>
          <Maquette couches={plan.couches} emprise={plan.emprise}
            arrets={mobilite && mobilite.etat === 'mesure' ? mobilite.couches?.arrets : undefined} />

          <div className="chiffres-cle">
            <div><strong>{nf(plan.bilan.ilots)}</strong><span>îlots</span></div>
            <div><strong>{nf(plan.bilan.logements)}</strong><span>logements</span></div>
            <div><strong>{nf(plan.bilan.habitants)}</strong><span>habitants</span></div>
            <div><strong>{plan.bilan.densiteNetteLogParHa}</strong><span>log/ha net</span></div>
            <div><strong>{plan.marchabilite.partIlotsBienDesservisPct} %</strong><span>îlots bien desservis à pied</span></div>
          </div>

          <h3>Équipements implantés et accès à pied</h3>
          <table className="table-normes">
            <tbody>
              {plan.equipements.filter((e) => e.requis > 0).map((e) => {
                const m = plan.marchabilite.parEquipement.find((x) => x.code === e.code);
                return (
                  <tr key={e.code}>
                    <td>{e.libelle}</td>
                    <td className={e.implantes < e.requis ? 'norme-non-etablie' : 'norme-etablie'}>
                      {e.implantes} / {e.requis}
                    </td>
                    <td className="part">{m ? `${m.ilotsDesservisPct} %` : '—'}</td>
                    <td className="ref-legale">
                      {m ? `à moins de ${m.rayonM} m · ${m.tempsMoyenMin} min en moyenne` : 'pas un trajet piéton'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {plan.avertissements.map((a) => <p key={a} className="avert-programme">{a}</p>)}
        </div>
      )}

      {infra && (
        <div className="resultat-terrain">
          <h3>Infrastructures — §21 étape 4</h3>
          {infra.etat !== 'mesure' ? (
            <div className="carte-alerte" role="alert">
              Normes absentes du référentiel : {(infra.manquantes ?? []).join(', ')}.
              Aucun besoin ne se chiffre sans elles.
            </div>
          ) : (
            <>
              <table className="table-normes">
                <tbody>
                  {infra.besoins?.map((b) => (
                    <tr key={b.code}>
                      <td>{b.libelle}</td>
                      <td className="part"><strong>{nf(b.valeur)} {b.unite}</strong></td>
                      <td className="ref-legale">{b.calcul}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <table className="table-normes">
                <tbody>
                  {infra.ouvrages?.map((o) => (
                    <tr key={o.code}>
                      <td>{o.libelle}</td>
                      <td className="part"><strong>{o.requis}</strong></td>
                      <td className="ref-legale">de {nf(o.capaciteUnitaire)} {o.unite} chacun</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {infra.avertissements?.map((a) => <p key={a} className="avert-programme">{a}</p>)}
            </>
          )}
        </div>
      )}

      {mobilite && (
        <div className="resultat-terrain">
          <h3>Mobilité — §21 étape 5, §23</h3>
          {mobilite.etat !== 'mesure' ? (
            <div className="carte-alerte" role="alert">
              Normes absentes du référentiel : {(mobilite.manquantes ?? []).join(', ')}.
            </div>
          ) : (
            <>
              <div className="chiffres-cle">
                <div><strong>{nf(mobilite.reseau!.lineairePrimaireM + mobilite.reseau!.lineaireSecondaireM)} m</strong><span>de voies dessinées</span></div>
                <div><strong>{mobilite.reseau!.densiteMaillageKmKm2}</strong><span>km de voie / km²</span></div>
                <div><strong>{mobilite.transportCollectif!.arrets}</strong><span>arrêts de TC posés</span></div>
                <div><strong>{mobilite.transportCollectif!.logementsDesservisPct} %</strong><span>de logements desservis (objectif {mobilite.transportCollectif!.objectifPct} %)</span></div>
                <div><strong>{mobilite.transportCollectif!.ilotsDesservisPct} %</strong><span>d'îlots desservis — information, pas la norme</span></div>
                <div><strong>{mobilite.tempsVersCentre!.aPiedMin} / {mobilite.tempsVersCentre!.enVehiculeMin} min</strong><span>vers le centre, à pied / en véhicule</span></div>
              </div>
              <p className={mobilite.transportCollectif!.objectifAtteint ? 'norme-etablie' : 'norme-non-etablie'}>
                Contrainte du §23 — « {mobilite.transportCollectif!.objectifPct} % des logements à moins de{' '}
                {mobilite.transportCollectif!.rayonAccesM} m d'un arrêt » :{' '}
                {/* ⚠️ La norme parle de LOGEMENTS : le % mesuré est la part de
                    logements — la part d'îlots la surestime (revue 12/10, C2). */}
                <strong>{mobilite.transportCollectif!.objectifAtteint ? 'atteinte' : 'NON atteinte'}</strong>
                {' '}({mobilite.transportCollectif!.logementsDesservisPct} % des logements mesurés).
              </p>
              {mobilite.avertissements?.map((a) => <p key={a} className="avert-programme">{a}</p>)}
            </>
          )}
        </div>
      )}

      {quartiers && (
        <div className="resultat-terrain">
          <h3>Quartiers — §21 étape 6</h3>
          {quartiers.etat !== 'mesure' ? (
            <div className="carte-alerte" role="alert">{quartiers.motif}</div>
          ) : (
            <>
              <p>
                <strong>{quartiers.quartiers!.length}</strong> quartiers formés autour des écoles
                primaires implantées · <strong>{quartiers.partQuartiersCompletsPct} %</strong> sont
                complètement dotés en équipements de proximité.
              </p>
              <table className="table-normes">
                <tbody>
                  {quartiers.quartiers!.map((q) => (
                    <tr key={q.id}>
                      <td>{q.id}</td>
                      <td className="part">{nf(q.habitants)} hab · {q.ilots} îlots</td>
                      <td className={q.manquants.length === 0 ? 'norme-etablie' : 'norme-non-etablie'}>
                        {q.manquants.length === 0 ? 'complet' : `manque : ${q.manquants.map((m) => m.libelle).join(', ')}`}
                      </td>
                      <td className="ref-legale">rayon vécu {q.rayonVecuM} m</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {quartiers.avertissements?.map((a) => <p key={a} className="avert-programme">{a}</p>)}
            </>
          )}
        </div>
      )}

      {batiments && (
        <div className="resultat-terrain">
          <h3>Bâtiments — §21 étape 9</h3>
          {batiments.etat !== 'mesure' ? (
            <div className="carte-alerte" role="alert">
              {batiments.etat === 'parcellaire_omis' ? batiments.motif
                : `Normes absentes : ${(batiments.manquantes ?? []).join(', ')}.`}
            </div>
          ) : (
            <>
              <div className="chiffres-cle">
                <div><strong>{nf(batiments.typologie!.residentiels)}</strong><span>bâtiments résidentiels</span></div>
                <div><strong>{nf(batiments.typologie!.mixtes)}</strong><span>mixtes (RDC commerçant)</span></div>
                <div><strong>{nf(batiments.typologie!.logements)}</strong><span>logements après ventilation</span></div>
                <div><strong>{nf(batiments.typologie!.commerces)}</strong><span>commerces</span></div>
              </div>
              {batiments.avertissements?.map((a) => <p key={a} className="avert-programme">{a}</p>)}
            </>
          )}
        </div>
      )}

      {reseaux && (
        <div className="resultat-terrain">
          <h3>Réseaux — §21 étape 10, §24</h3>
          {reseaux.etat !== 'mesure' ? (
            <div className="carte-alerte" role="alert">
              {reseaux.etat === 'besoins_absents' ? reseaux.motif
                : `Normes absentes : ${(reseaux.manquantes ?? []).join(', ')}.`}
            </div>
          ) : (
            <>
              <p className={reseaux.drainage!.gravitaire ? 'norme-etablie' : 'norme-non-etablie'}>
                {reseaux.drainage!.lecture}
              </p>
              <table className="table-normes">
                <tbody>
                  {reseaux.reseaux!.map((x) => (
                    <tr key={x.code}>
                      <td>{x.libelle}</td>
                      <td className="part">{nf(x.lineaireM)} m{x.points ? ` · ${nf(x.points.nombre)} ${x.points.libelle}` : ''}</td>
                      <td className="ref-legale">{x.charge}</td>
                      <td className="ref-legale">
                        {x.coutOrdreGrandeurGNF !== null
                          ? `≈ ${nf(Math.round(x.coutOrdreGrandeurGNF / 1e9))} Mrd GNF — ordre de grandeur non fondé`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {reseaux.avertissements?.map((a) => <p key={a} className="avert-programme">{a}</p>)}
            </>
          )}
        </div>
      )}

      {s && s.climat && (
        <div className="resultat-terrain">
          <h3>Climat et résilience — §25</h3>
          <div className="chiffres-cle">
            <div><strong>{s.climat.impermeabilisationPct} %</strong><span>imperméabilisé</span></div>
            <div><strong>{s.climat.partEspacesPublicsPct} %</strong><span>d'îlots en espace public</span></div>
            <div><strong>{nf(s.climat.inondation.cuvettes)}</strong><span>dépressions fermées</span></div>
            <div><strong>{nf(s.climat.inondation.pointsBasMailles)}</strong><span>mailles en point bas</span></div>
          </div>
          <p className="avert-programme">{s.climat.inondation.lecture}</p>
          <p className="avert-programme">{s.climat.lecture}</p>
        </div>
      )}

      {s && s.indicateurs && (
        <div className="resultat-terrain">
          <h3>Indicateurs — §28, chaque nombre dit d'où il vient</h3>
          <table className="table-normes">
            <tbody>
              {s.indicateurs.map((i) => (
                <tr key={i.code}>
                  <td>{i.libelle}</td>
                  <td className="part"><strong>{nf(i.valeur)} {i.unite}</strong></td>
                  <td className={i.confiance === 'mesure' ? 'norme-etablie' : 'norme-non-etablie'}>
                    {i.confiance === 'mesure' ? 'mesure' : 'hypothèse de travail'}
                  </td>
                  <td className="ref-legale">{i.source.replace(/_/g, ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {contraintes && (
        <div className="resultat-terrain">
          <h3>Terrain et contraintes — §21 étapes 1-2</h3>
          <div className="chiffres-cle">
            <div><strong>{nf(contraintes.surfaceHa)} ha</strong><span>emprise</span></div>
            <div><strong>{contraintes.altitude.minM} – {contraintes.altitude.maxM} m</strong><span>altitude</span></div>
            <div><strong>{contraintes.pente.moyennePct} %</strong><span>pente moyenne</span></div>
            <div><strong>{contraintes.pente.urbanisablePct} %</strong><span>sous 10 % de pente</span></div>
          </div>
          <h3>Ce que cette mesure ne dit pas</h3>
          <ul className="non-connu">
            {contraintes.nonConnu.map((l) => <li key={l}>{l}</li>)}
          </ul>
          <p className="attribution-mesure">{contraintes.attribution} {contraintes.responsabilite}</p>
        </div>
      )}

      {programme && (
        <div className="resultat-terrain">
          <h3>Programme urbain — §21 étape 3</h3>
          <div className="chiffres-cle">
            <div><strong>{nf(programme.terrain.amenageableHa)} ha</strong><span>aménageable</span></div>
            <div><strong>{nf(programme.capacite.logements)}</strong><span>logements (programme)</span></div>
            <div><strong>{programme.capacite.densiteBruteHabParHa}</strong><span>hab/ha</span></div>
          </div>
          <h3>Ce qui occupe déjà le sol{mode === 'transformer' ? ' — la matière du mode « transformer »' : ''}</h3>
          <p>
            <strong>{nf(programme.occupation.batiments)}</strong> bâtiments
            ({nf(programme.occupation.batimentsHa)} ha, soit{' '}
            {programme.occupation.partBatieDeLAmenageablePct} % de l'aménageable) ·{' '}
            <strong>{nf(programme.occupation.parcelles)}</strong> parcelles,
            dont <strong>{nf(programme.occupation.parcellesAvecDroit)}</strong> portant un droit inscrit.
          </p>
          <h3>Les normes consommées</h3>
          <table className="table-normes">
            <tbody>
              {programme.normes.map((n) => (
                <tr key={n.code}>
                  <td>{n.libelle}</td>
                  <td className="part">{n.valeur} {n.unite}</td>
                  <td className={n.sourceEtablie ? 'norme-etablie' : 'norme-non-etablie'}>
                    {n.sourceEtablie ? 'établie' : 'non établie'}
                  </td>
                  <td className="ref-legale">{n.referenceLegale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── LES SCÉNARIOS PERSISTÉS — §20 et §28 ──────────────────────────── */}
      <div className="resultat-terrain">
        <h3>Scénarios — §28 : une étude sans hypothèses n'est pas un scénario</h3>
        {s ? (
          <div className="zone-saisie">
            <label className="champ-zone">
              <span>Titre</span>
              <input type="text" value={titre} maxLength={200}
                onChange={(ev) => setTitre(ev.target.value)} />
            </label>
            <label className="champ-zone">
              <span>Hypothèses (obligatoires)</span>
              <input type="text" value={hypotheses} maxLength={5000}
                placeholder="Ce que ce scénario suppose — densité visée, horizon, financement…"
                onChange={(ev) => setHypotheses(ev.target.value)} />
            </label>
            <button className="bouton-principal" onClick={enregistrer}
              disabled={depotEnCours || titre.trim().length < 3 || hypotheses.trim().length < 10}>
              {depotEnCours ? 'Enregistrement…' : `Enregistrer ce scénario (${mode})`}
            </button>
          </div>
        ) : (
          <p className="avert-programme">
            Simulez d'abord : le serveur refait la chaîne entière au dépôt et fige SON bilan —
            jamais celui de l'écran.
          </p>
        )}
        {depotMessage && <p className="avert-programme" role="status">{depotMessage}</p>}

        {scenariosErreur && <p className="avert-programme" role="alert">{scenariosErreur}</p>}
        {/* ⚠️ L'ÉTAT VIDE SE DIT : une lecture réussie à zéro scénario était
            indiscernable d'un refus également muet (revue 12/10, C14). Un refus
            pose scenarios=null — cette ligne ne parle que du vide réel. */}
        {scenarios && scenarios.length === 0 && !scenariosErreur && (
          <p className="avert-programme">Aucun scénario déposé pour l'instant.</p>
        )}
        {scenarios && scenarios.length > 0 && (
          <>
          {scenariosTotal > scenarios.length && (
            <p className="avert-programme">
              {scenarios.length} scénarios affichés sur {scenariosTotal} : la liste est tronquée aux plus récents.
            </p>
          )}
          <table className="table-normes">
            <tbody>
              {scenarios.map((x) => (
                <tr key={x.reference}>
                  <td>{x.reference}</td>
                  <td>{x.titre}</td>
                  <td className="part">{x.mode}</td>
                  <td className={x.viable ? 'norme-etablie' : 'norme-non-etablie'}>
                    {x.statut === 'retire' ? 'retiré' : x.viable === null ? '—' : x.viable ? 'viable' : 'non viable'}
                  </td>
                  <td className="ref-legale">{new Date(x.creeLe).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <button className="bouton-principal" onClick={() => comparer(x.reference)}>
                      {compares.some((f) => f.reference === x.reference) ? 'Retirer de la comparaison' : 'Comparer'}
                    </button>
                    {x.statut === 'actif' && (
                      <button className="bouton-principal" onClick={() => retirer(x.reference)}>
                        {'Retirer ' + x.reference}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}

        {compares.length >= 2 && (
          <>
            <h3>Comparaison — §28</h3>
            <table className="table-normes">
              <tbody>
                <tr>
                  <td>Indicateur</td>
                  {compares.map((f) => <td key={f.reference} className="part"><strong>{f.reference}</strong></td>)}
                </tr>
                {/* ⚠️ L'UNION des codes, jamais ceux du seul premier : un
                    indicateur absent du scénario de gauche disparaîtrait pour
                    TOUS, en silence. */}
                {[...new Map(compares.flatMap((f) => f.bilan.indicateurs.map((i) => [i.code, i] as const))).values()]
                  .map((i) => (
                  <tr key={i.code}>
                    <td>{i.libelle}</td>
                    {compares.map((f) => {
                      const x = f.bilan.indicateurs.find((y) => y.code === i.code);
                      return <td key={f.reference} className="part">{x ? `${nf(x.valeur)} ${x.unite}` : '—'}</td>;
                    })}
                  </tr>
                ))}
                <tr>
                  <td>Viabilité (§24)</td>
                  {compares.map((f) => (
                    <td key={f.reference}
                      className={f.bilan.viabilite.viable ? 'norme-etablie' : 'norme-non-etablie'}>
                      {f.bilan.viabilite.viable ? 'viable' : 'non viable'}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>Hypothèses</td>
                  {compares.map((f) => <td key={f.reference} className="ref-legale">{f.hypotheses}</td>)}
                </tr>
              </tbody>
            </table>
          </>
        )}
      </div>
    </section>
  );
}
